<script setup lang="ts">
/**
 * BuildVersionBadge - компактная строка с версиями frontend и worker.
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface WorkerVersionResponse {
  version: string
  displayVersion: string
}

const { t } = useI18n()

const backendRawVersion = ref<string | null>(null)
const backendDisplayVersion = ref<string | null>(null)
const isBackendLoading = ref(true)
const frontendRawVersion = import.meta.env.VITE_APP_VERSION ?? null

const frontendDisplayVersion = computed(() => {
  return frontendRawVersion ? formatVersion(frontendRawVersion) : t('public.landing.versionBadge.unavailable')
})

const backendVersionText = computed(() => {
  if (isBackendLoading.value) {
    return t('public.landing.versionBadge.loading')
  }

  return backendDisplayVersion.value ?? t('public.landing.versionBadge.unavailable')
})

function formatVersion(version: string): string {
  const trimmed = version.trim()
  if (/^[0-9a-f]{40}$/i.test(trimmed)) {
    return trimmed.slice(0, 8)
  }

  return trimmed
}

async function loadBackendVersion(signal: AbortSignal): Promise<void> {
  try {
    const response = await fetch('/api/v1/worker/version', {
      cache: 'no-store',
      signal,
    })

    if (!response.ok) {
      return
    }

    const payload = await response.json() as WorkerVersionResponse
    backendRawVersion.value = payload.version
    backendDisplayVersion.value = payload.displayVersion || formatVersion(payload.version)
  } catch {
    // Ignore network errors and render the fallback state.
  } finally {
    if (!signal.aborted) {
      isBackendLoading.value = false
    }
  }
}

const abortController = new AbortController()

onMounted(() => {
  void loadBackendVersion(abortController.signal)
})

onUnmounted(() => {
  abortController.abort()
})
</script>

<template>
  <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-base-content/28">
    <span class="uppercase tracking-[0.18em] text-base-content/24">
      {{ t('public.landing.versionBadge.title') }}
    </span>
    <span class="flex items-center gap-1">
      <span>{{ t('public.landing.versionBadge.frontend') }}</span>
      <code
        class="font-semibold text-base-content/45"
        :title="frontendRawVersion ?? undefined"
      >
        {{ frontendDisplayVersion }}
      </code>
    </span>
    <span class="flex items-center gap-1">
      <span>{{ t('public.landing.versionBadge.worker') }}</span>
      <code
        class="font-semibold text-base-content/45"
        :title="backendRawVersion ?? undefined"
      >
        {{ backendVersionText }}
      </code>
    </span>
  </div>
</template>
